import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import Seo from "../../components/Seo";
import MortgageDisclosure from "../../components/MortgageDisclosure";
import { EDGE_NAVY, EDGE_APP_URL } from "../../lib/edge";

/**
 * DSCR Calculator
 * --------------------------------------------------
 * Most lenders calculate DSCR as:
 *   DSCR = Gross Monthly Rent / Monthly PITIA
 * where PITIA = Principal + Interest + Taxes + Insurance + (HOA + Flood, optional)
 *
 * Cash-on-Cash Return (annualized) = (NOI - debt service) * 12 / total cash invested
 * NOI is approximated here as Gross Rent * (1 - vacancy%) - operating expenses (taxes + ins + HOA).
 */

const tierFor = (dscr) => {
  if (dscr >= 1.25) return { label: "Strong (≥ 1.25)", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", note: "Best pricing tier. Most DSCR programs offer their lowest rates here." };
  if (dscr >= 1.10) return { label: "Standard (≥ 1.10)", color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", note: "Solid qualifying ratio for most DSCR programs." };
  if (dscr >= 1.00) return { label: "Break-even (≥ 1.00)", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", note: "Property covers its debt — eligible on most programs with standard pricing." };
  if (dscr >= 0.75) return { label: "Sub-1.0 (0.75 – 0.99)", color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200", note: "Eligible on 'No-Ratio' DSCR programs at higher pricing & larger down." };
  return { label: "Below 0.75", color: "text-red-700", bg: "bg-red-50", border: "border-red-200", note: "Difficult to qualify without higher down payment or rent increase." };
};

const pmtMonthly = (principal, annualRatePct, years) => {
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
};

const fmt = (n) =>
  isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";

export default function DscrCalculator() {
  const [purchasePrice, setPurchasePrice] = useState(450000);
  const [downPct, setDownPct] = useState(25);
  const [rate, setRate] = useState(7.5);
  const [term, setTerm] = useState(30);
  const [monthlyRent, setMonthlyRent] = useState(3600);
  const [annualTaxPct, setAnnualTaxPct] = useState(2.4); // Houston typical
  const [annualInsurance, setAnnualInsurance] = useState(2400);
  const [monthlyHoa, setMonthlyHoa] = useState(0);
  const [vacancyPct, setVacancyPct] = useState(8);
  const [closingCostsPct, setClosingCostsPct] = useState(3);

  const calc = useMemo(() => {
    const downPayment = (purchasePrice * downPct) / 100;
    const loanAmount = Math.max(0, purchasePrice - downPayment);
    const pi = pmtMonthly(loanAmount, rate, term);
    const monthlyTax = (purchasePrice * (annualTaxPct / 100)) / 12;
    const monthlyIns = annualInsurance / 12;
    const pitia = pi + monthlyTax + monthlyIns + Number(monthlyHoa || 0);

    const dscr = pitia > 0 ? monthlyRent / pitia : 0;
    const effectiveRent = monthlyRent * (1 - vacancyPct / 100);
    const monthlyCashFlow = effectiveRent - pitia;
    const annualCashFlow = monthlyCashFlow * 12;

    const closingCosts = (purchasePrice * closingCostsPct) / 100;
    const totalCashInvested = downPayment + closingCosts;
    const cashOnCash =
      totalCashInvested > 0 ? (annualCashFlow / totalCashInvested) * 100 : 0;

    const breakEvenRent = pitia / (1 - vacancyPct / 100);

    return {
      downPayment,
      loanAmount,
      pi,
      monthlyTax,
      monthlyIns,
      pitia,
      dscr,
      effectiveRent,
      monthlyCashFlow,
      annualCashFlow,
      closingCosts,
      totalCashInvested,
      cashOnCash,
      breakEvenRent,
    };
  }, [purchasePrice, downPct, rate, term, monthlyRent, annualTaxPct, annualInsurance, monthlyHoa, vacancyPct, closingCostsPct]);

  const tier = tierFor(calc.dscr);

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="dscr-calc-page">
      <Seo
        title="DSCR Calculator — Rental Property Cash Flow"
        description="Estimate DSCR (Debt-Service Coverage Ratio), monthly cash flow, and cash-on-cash return on a Houston rental property. By Edge Home Finance · Pierre Joseph NMLS #1978260."
        path="/dscr-calculator"
      />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: EDGE_NAVY }}>
          Edge Home Finance · Investor Tools
        </div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-[#1A1A1A] max-w-3xl">
          DSCR <em className="not-italic" style={{ color: EDGE_NAVY }}>calculator</em>.
        </h1>
        <p className="text-[#555] mt-5 max-w-2xl text-lg">
          Plug in a Houston rental and instantly see your Debt-Service Coverage Ratio, monthly cash flow, and cash-on-cash return. Used by lenders to qualify DSCR loans without tax returns.
        </p>
      </section>

      {/* Calc body */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Inputs */}
          <div className="lg:col-span-7 bg-white border border-gray-200 rounded-2xl p-8">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="w-5 h-5" style={{ color: EDGE_NAVY }} />
              <h2 className="font-display text-2xl font-semibold">The deal</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Purchase price" value={purchasePrice} onChange={setPurchasePrice} prefix="$" step={5000} testId="dscr-price" />
              <Field label="Down payment %" value={downPct} onChange={setDownPct} suffix="%" step={1} max={100} testId="dscr-downpct" />
              <Field label="Interest rate" value={rate} onChange={setRate} suffix="%" step={0.125} max={20} testId="dscr-rate" />
              <Field label="Term (years)" value={term} onChange={setTerm} step={5} max={40} testId="dscr-term" />
              <Field label="Gross monthly rent" value={monthlyRent} onChange={setMonthlyRent} prefix="$" step={50} testId="dscr-rent" />
              <Field label="Vacancy %" value={vacancyPct} onChange={setVacancyPct} suffix="%" step={1} max={50} testId="dscr-vacancy" />
              <Field label="Property tax (annual %)" value={annualTaxPct} onChange={setAnnualTaxPct} suffix="%" step={0.1} max={10} testId="dscr-tax" />
              <Field label="Annual insurance" value={annualInsurance} onChange={setAnnualInsurance} prefix="$" step={100} testId="dscr-ins" />
              <Field label="Monthly HOA / flood" value={monthlyHoa} onChange={setMonthlyHoa} prefix="$" step={25} testId="dscr-hoa" />
              <Field label="Closing costs %" value={closingCostsPct} onChange={setClosingCostsPct} suffix="%" step={0.5} max={10} testId="dscr-closing" />
            </div>

            <div className="mt-7 bg-stone-50 border border-gray-200 rounded-xl p-5 text-sm text-[#444] leading-relaxed">
              <strong className="text-[#1A1A1A]">Houston defaults:</strong> ~2.2–2.7% effective property tax (no state income tax). Insurance is higher than the U.S. average due to wind/hail; budget $1,800–$3,500/yr on a single family. Vacancy assumption 8% (a month of turnover per year).
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-5 space-y-5">
            <div className={`rounded-2xl p-7 border-2 ${tier.border} ${tier.bg}`} data-testid="dscr-result-card">
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-600 mb-2">Your DSCR</div>
              <div className={`font-display text-6xl font-semibold ${tier.color}`} data-testid="dscr-value">
                {isFinite(calc.dscr) ? calc.dscr.toFixed(2) : "—"}
              </div>
              <div className={`mt-2 font-semibold ${tier.color}`}>{tier.label}</div>
              <p className="text-sm text-[#444] mt-3 leading-relaxed">{tier.note}</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-4">Monthly breakdown</div>
              <ResultRow label="Principal & Interest" value={fmt(calc.pi)} />
              <ResultRow label="Property Tax" value={fmt(calc.monthlyTax)} />
              <ResultRow label="Insurance" value={fmt(calc.monthlyIns)} />
              <ResultRow label="HOA / Flood" value={fmt(Number(monthlyHoa))} />
              <div className="border-t border-gray-200 my-3" />
              <ResultRow label="Monthly PITIA" value={fmt(calc.pitia)} bold />
              <ResultRow label="Effective Rent" value={fmt(calc.effectiveRent)} subtle />
              <ResultRow
                label="Cash Flow / month"
                value={fmt(calc.monthlyCashFlow)}
                bold
                positive={calc.monthlyCashFlow > 0}
                negative={calc.monthlyCashFlow < 0}
              />
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-4">Investment summary</div>
              <ResultRow label="Loan Amount" value={fmt(calc.loanAmount)} />
              <ResultRow label="Down Payment" value={fmt(calc.downPayment)} />
              <ResultRow label="Closing Costs (est.)" value={fmt(calc.closingCosts)} />
              <ResultRow label="Total Cash Invested" value={fmt(calc.totalCashInvested)} bold />
              <div className="border-t border-gray-200 my-3" />
              <ResultRow label="Annual Cash Flow" value={fmt(calc.annualCashFlow)} positive={calc.annualCashFlow > 0} />
              <ResultRow
                label="Cash-on-Cash Return"
                value={isFinite(calc.cashOnCash) ? `${calc.cashOnCash.toFixed(2)}%` : "—"}
                bold
                positive={calc.cashOnCash > 0}
                negative={calc.cashOnCash < 0}
              />
              <ResultRow label="Break-even Rent" value={fmt(calc.breakEvenRent)} subtle />
            </div>

            {/* Verdict pill */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-start gap-3">
              {calc.dscr >= 1.0 ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              )}
              <div className="text-sm text-[#1A1A1A] leading-relaxed">
                {calc.dscr >= 1.25 && "This deal looks excellent for DSCR financing. You'll likely qualify for best pricing tiers."}
                {calc.dscr >= 1.10 && calc.dscr < 1.25 && "Good DSCR. Standard pricing on most DSCR programs."}
                {calc.dscr >= 1.0 && calc.dscr < 1.10 && "The property covers its debt. Eligible on standard DSCR — pricing slightly higher than 1.10+ tier."}
                {calc.dscr >= 0.75 && calc.dscr < 1.0 && "Sub-1.0 — you'd need a No-Ratio DSCR program (larger down, higher rate). Or increase rent / lower down."}
                {calc.dscr < 0.75 && "Negative cash flow. Consider increasing the down payment, finding a higher-rent property, or negotiating the price."}
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-[#1A1A1A] text-white rounded-2xl p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-3">Ready to scale your portfolio?</div>
            <h2 className="font-display text-3xl lg:text-4xl font-light tracking-tighter">
              Get a DSCR pre-approval from <em className="not-italic text-[#E63946]">Pierre</em>.
            </h2>
            <p className="text-white/80 mt-4 max-w-xl">
              No tax returns. No W-2s. Qualify on the property&apos;s cash flow — title in your name or an LLC. Unlimited properties.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link
              to="/pre-approval"
              state={{ program: "DSCR" }}
              className="bg-[#E63946] hover:bg-[#c9313d] text-white px-7 py-3.5 rounded-md font-semibold text-sm text-center"
              data-testid="dscr-cta-preapproval"
            >
              Start DSCR Pre-Approval
            </Link>
            <a
              href={EDGE_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 hover:bg-white/15 text-white px-7 py-3.5 rounded-md font-semibold text-sm text-center border border-white/20"
              data-testid="dscr-cta-1003"
            >
              Apply Online (1003) <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
            </a>
          </div>
        </div>

        {/* What DSCR is */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[
            {
              icon: <TrendingUp className="w-5 h-5" />,
              t: "How lenders calculate DSCR",
              d: "DSCR = Gross Monthly Rent ÷ Monthly PITIA. Most programs want ≥ 1.0; the best pricing kicks in at 1.25+.",
            },
            {
              icon: <Calculator className="w-5 h-5" />,
              t: "No personal income docs",
              d: "DSCR loans qualify on the property's cash flow — no W-2s, paystubs, or tax returns required. Great for self-employed investors.",
            },
            {
              icon: <CheckCircle2 className="w-5 h-5" />,
              t: "LLC titling & STR friendly",
              d: "Title in your name or an LLC. Many programs accept Airbnb / VRBO short-term rental income for the DSCR calc.",
            },
          ].map((c) => (
            <div key={c.t} className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${EDGE_NAVY}1A`, color: EDGE_NAVY }}>
                {c.icon}
              </div>
              <div className="font-display text-lg font-semibold mb-2">{c.t}</div>
              <p className="text-sm text-[#555] leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <MortgageDisclosure />
    </div>
  );
}

function Field({ label, value, onChange, prefix, suffix, step = 1, max, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1.5">{label}</span>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">{prefix}</span>
        )}
        <input
          type="number"
          value={value}
          step={step}
          max={max}
          min={0}
          onChange={(e) => {
            const v = e.target.value === "" ? 0 : Number(e.target.value);
            onChange(v);
          }}
          className={`w-full ${prefix ? "pl-7" : "pl-3"} ${suffix ? "pr-9" : "pr-3"} py-2.5 bg-stone-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E63946] focus:bg-white`}
          data-testid={testId}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">{suffix}</span>
        )}
      </div>
    </label>
  );
}

function ResultRow({ label, value, bold, subtle, positive, negative }) {
  const color = positive
    ? "text-emerald-700"
    : negative
    ? "text-red-700"
    : subtle
    ? "text-gray-500"
    : "text-[#1A1A1A]";
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={`${bold ? "font-semibold" : ""} ${color}`}>{value}</span>
    </div>
  );
}
